/** Generate brand shade scales as HSL triples for the application CSS tokens. */

type Hsl = { h: number; s: number; l: number };

export function hexToHsl(hex: string): Hsl {
	const normalized = hex.replace("#", "");
	const full =
		normalized.length === 3
			? normalized
					.split("")
					.map((c) => c + c)
					.join("")
			: normalized;

	const r = Number.parseInt(full.slice(0, 2), 16) / 255;
	const g = Number.parseInt(full.slice(2, 4), 16) / 255;
	const b = Number.parseInt(full.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	if (max === min) return { h: 0, s: 0, l: l * 100 };

	const d = max - min;
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

	let h: number;
	switch (max) {
		case r:
			h = (g - b) / d + (g < b ? 6 : 0);
			break;
		case g:
			h = (b - r) / d + 2;
			break;
		default:
			h = (r - g) / d + 4;
	}
	h *= 60;

	return { h, s: s * 100, l: l * 100 };
}

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

const toTriple = ({ h, s, l }: Hsl) =>
	`${round(h)} ${round(clamp(s, 0, 100))}% ${round(clamp(l, 0, 100))}%`;

const round = (value: number) => Math.round(value * 100) / 100;

/** WCAG relative luminance, used to pick a readable foreground (white/near-black). */
function relativeLuminance(hex: string): number {
	const normalized = hex.replace("#", "");
	const full =
		normalized.length === 3
			? normalized
					.split("")
					.map((c) => c + c)
					.join("")
			: normalized;

	const channel = (value: number) => {
		const c = value / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};

	const r = channel(Number.parseInt(full.slice(0, 2), 16));
	const g = channel(Number.parseInt(full.slice(2, 4), 16));
	const b = channel(Number.parseInt(full.slice(4, 6), 16));

	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export type ColorScale = {
	50: string;
	100: string;
	200: string;
	300: string;
	400: string;
	500: string;
	600: string;
	700: string;
	800: string;
	900: string;
	DEFAULT: string;
	foreground: string;
};

/**
 * Generate a full 50–900 shade scale from one base hex. Lightness stops are
 * fixed targets (Tailwind-esque) so the ramp stays usable regardless of how
 * light/dark the user's chosen base color is; hue/saturation are preserved
 * from the base with a light desaturation at the extremes.
 */
export function generateColorScale(hex: string): ColorScale {
	const base = hexToHsl(hex);

	const stop = (l: number, sAdjust = 0): string =>
		toTriple({ h: base.h, s: clamp(base.s + sAdjust, 0, 100), l });

	const foreground =
		relativeLuminance(hex) > 0.55 ? "240 5.9% 10%" : "0 0% 100%";

	return {
		50: stop(97, -8),
		100: stop(94, -6),
		200: stop(87, -4),
		300: stop(78, -2),
		400: stop(68),
		500: toTriple(base),
		600: stop(clamp(base.l - 8, 15, 90)),
		700: stop(clamp(base.l - 18, 12, 85), 4),
		800: stop(clamp(base.l - 28, 10, 80), 6),
		900: stop(clamp(base.l - 38, 8, 75), 8),
		DEFAULT: toTriple(base),
		foreground,
	};
}

/** Basic hex validation for user-typed custom colors. */
export function isValidHex(value: string): boolean {
	return /^#?[0-9a-fA-F]{3}$|^#?[0-9a-fA-F]{6}$/.test(value.trim());
}

export function normalizeHex(value: string): string {
	const trimmed = value.trim();
	return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}
